import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Checkbox, 
  TextField, 
  Button, 
  Alert, 
  CircularProgress, 
  Box,
  Typography,
  Paper,
  IconButton,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { API_ENDPOINTS } from '../../../../config/api';

const Roles = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: '/roles' } });
      return;
    }
    fetchRoles();
    fetchPermissions();
  }, [navigate]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login', { state: { from: '/roles' } });
        return;
      }

      const response = await axios.get(API_ENDPOINTS.ROLES.BASE, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      console.log('Roles response:', response.data);
      setRoles(response.data.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        navigate('/login', { state: { from: '/roles' } });
      } else {
        setError(error.response?.data?.message || 'Failed to fetch roles');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login', { state: { from: '/roles' } });
        return;
      }

      const response = await axios.get(API_ENDPOINTS.PERMISSIONS.BASE, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      const permissionsData = Array.isArray(response.data) ? response.data : response.data.data || [];
      setPermissions(permissionsData);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        navigate('/login', { state: { from: '/roles' } });
      } else {
        setError(error.response?.data?.message || 'Failed to fetch permissions');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleCreateRole = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login', { state: { from: '/roles' } });
        return;
      }

      // First create the role
      const roleResponse = await axios.post(API_ENDPOINTS.ROLES.BASE, {
        name: roleName
      }, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      const roleId = roleResponse.data.data.id;

      // Then attach permissions to the role
      await axios.post(API_ENDPOINTS.ROLES.PERMISSIONS(roleId), {
        permissions: selectedPermissions
      }, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      fetchRoles(); // Refresh the roles list
    } catch (error) {
      console.error('Error creating role:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        navigate('/login', { state: { from: '/roles' } });
      } else {
        setError(error.response?.data?.message || 'Failed to create role');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login', { state: { from: '/roles' } });
        return;
      }

      await axios.delete(`${API_ENDPOINTS.ROLES.BASE}/${roleId}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      fetchRoles(); // Refresh the roles list
    } catch (error) {
      console.error('Error deleting role:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        navigate('/login', { state: { from: '/roles' } });
      } else {
        setError(error.response?.data?.message || 'Failed to delete role');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRolePage = () => {
    navigate('/roles/create');
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Roles Management
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateRolePage}
            >
              Create Role
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : roles.length === 0 ? (
            <Alert severity="info">No roles found</Alert>
          ) : (
            <TableContainer className="custom-table">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                {roles.map((role,index) => (
                    <TableRow key={role.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{role.name}</TableCell>
                      {/* <TableCell>
                        {role.permissions?.map(permission => permission.name).join(', ') || 'No permissions'}
                      </TableCell> */}
                      <TableCell>
                        <IconButton 
                          color="primary" 
                          onClick={() => navigate(`/roles/edit/${role.id}`)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          color="error"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this role?')) {
                              handleDeleteRole(role.id);
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default Roles;